import React, { useRef, useEffect } from 'react';
import type { LiveFeedItem } from '../api/simStudioApi';
import type { Theme } from './StudioHeader';
import { IconWifi, IconLogOut, IconClipboard, IconAlarm, IconBell } from './Icons';

interface Props {
  feed: LiveFeedItem[];
  loading: boolean;
  theme: Theme;
}

interface ItemMeta {
  Icon: React.FC<{ size?: number; color?: string }>;
  accent: string;
  label: string;
}

const TIPO_META: Record<string, ItemMeta> = {
  RFID_ENTRADA:      { Icon: IconWifi,      accent: '#4ade80', label: 'RFID Entrada' },
  RFID_SAIDA:        { Icon: IconLogOut,    accent: '#60a5fa', label: 'RFID Saída'   },
  CHAMADA_CONCLUIDA: { Icon: IconClipboard, accent: '#a78bfa', label: 'Chamada'      },
  CHAMADA_ABERTA:    { Icon: IconClipboard, accent: '#fbbf24', label: 'Chamada'      },
  AUSENCIA:          { Icon: IconAlarm,     accent: '#f87171', label: 'Ausência'     },
};
const FALLBACK: ItemMeta = { Icon: IconBell, accent: '#94a3b8', label: 'Evento' };

function hexToRgb(hex: string) {
  return `${parseInt(hex.slice(1,3),16)},${parseInt(hex.slice(3,5),16)},${parseInt(hex.slice(5,7),16)}`;
}

function timeAgo(ts: string) {
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return `${s}s atrás`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m atrás`;
  return `${Math.floor(m / 60)}h atrás`;
}

export function LiveFeedPanel({ feed, loading, theme }: Props) {
  const isDark = theme === 'dark';
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [feed.length]);

  const liveColor    = isDark ? '#4ade80' : '#16a34a';
  const headingColor = isDark ? '#94a3b8'  : '#475569';
  const subColor     = isDark ? '#475569'  : '#94a3b8';
  const emptyColor   = isDark ? '#374151'  : '#94a3b8';
  const labelColor   = isDark ? '#e2e8f0'  : '#0f172a';
  const sublabColor  = isDark ? '#64748b'  : '#64748b';
  const timeColor    = isDark ? '#374151'  : '#94a3b8';
  const skeletonBg   = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
  const headerBorder = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-3.5 flex-shrink-0"
        style={{ borderBottom: `1px solid ${headerBorder}` }}
      >
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
              style={{ background: liveColor }} />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5"
              style={{ background: liveColor }} />
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: headingColor }}>
            Feed Ao Vivo
          </span>
        </div>
        <span style={{ fontSize: 11, color: subColor }}>
          {feed.length} eventos · polling 5s
        </span>
      </div>

      {/* Lista */}
      <div ref={listRef} className="flex-1 overflow-y-auto min-h-0" style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>

        {loading && feed.length === 0 &&
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{ height: 64, borderRadius: 12, background: skeletonBg }} className="animate-pulse" />
          ))
        }

        {!loading && feed.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center py-16">
            <IconBell size={36} color={emptyColor} />
            <p style={{ fontSize: 14, fontWeight: 600, color: emptyColor, marginTop: 12 }}>
              Aguardando eventos ao vivo...
            </p>
            <p style={{ fontSize: 12, color: subColor, marginTop: 4 }}>
              Ative um cenário ou aguarde atividade escolar
            </p>
          </div>
        )}

        {feed.map((item, idx) => {
          const meta = TIPO_META[item.tipo] ?? FALLBACK;
          const rgb  = hexToRgb(meta.accent);
          const isNew = idx < 3;

          return (
            <div
              key={item.id}
              className="flex items-center gap-3"
              style={{
                background: isDark ? `rgba(${rgb},0.07)` : `rgba(${rgb},0.05)`,
                border: `1px solid rgba(${rgb},${isDark ? '0.18' : '0.14'})`,
                borderRadius: 12,
                padding: '12px 16px',
                animation: isNew ? 'feedSlideIn 0.3s ease' : undefined,
              }}
            >
              {/* Ícone monocromático */}
              <div style={{
                width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `rgba(${rgb}, ${isDark ? '0.15' : '0.1'})`,
              }}>
                <meta.Icon size={18} color={meta.accent} />
              </div>

              {/* Textos */}
              <div className="flex-1 min-w-0">
                <div style={{ fontSize: 14, fontWeight: 600, color: labelColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.label}
                </div>
                <div style={{ fontSize: 12, color: sublabColor, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.sublabel}
                </div>
              </div>

              {/* Badge + tempo */}
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <div style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                  padding: '2px 8px', borderRadius: 999,
                  background: `rgba(${rgb},0.15)`,
                  color: meta.accent,
                  textTransform: 'uppercase',
                }}>
                  {meta.label}
                </div>
                <div style={{ fontSize: 11, color: timeColor, tabularNums: true } as any}>
                  {timeAgo(item.ts)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes feedSlideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
